import React, {useState} from "react"
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material"
import BlockIcon from "@mui/icons-material/Block"

import {dismissalReasonIDs, HANDLED_ELSEWHERE, phraseDismissalReason} from "./chips"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

// Assigning is the everyday path and each candidate carries its own button, so
// the dismissal sits behind a deliberate step rather than beside them: an agent
// closing a message out of the queue should not be one misplaced click from it.
function DismissPanel({
	channel,
	conversationID,
	providerMessageID,
	agentEmail,
	onDismissed,
}: {
	channel: string
	conversationID: string
	providerMessageID: string
	agentEmail: string | undefined
	onDismissed: () => void
}) {
	const {notification} = useNotification()
	const [open, setOpen] = useState(false)
	const [reason, setReason] = useState("")
	const [note, setNote] = useState("")
	const [handOffTicked, setHandOffTicked] = useState(false)
	const [dismissing, setDismissing] = useState(false)

	// Handled elsewhere is the one ground that names somebody else already
	// serving the customer, so it is the only one offered a hand-off, and the
	// hand-off is what an agent picking it almost always means.
	const handOffOffered = reason === HANDLED_ELSEWHERE
	const handingOff = handOffOffered && handOffTicked

	const chooseReason = (id: string) => {
		setReason(id)
		setHandOffTicked(id === HANDLED_ELSEWHERE)
	}

	// Reopening on a fresh message must not inherit the last one's answers.
	const openDialog = () => {
		setReason("")
		setNote("")
		setHandOffTicked(false)
		setOpen(true)
	}

	const handleDismiss = async () => {
		setDismissing(true)

		try {
			await api.inbox.dismissUnidentified(channel, conversationID, providerMessageID, {
				Reason: reason,
				Note: note,
				HandedOff: handingOff,
				AgentEmail: agentEmail,
			})

			notification.success(handingOff ? "Message handed off" : "Message dismissed")
			// The panel closes its own dialog rather than leaving it to whatever
			// onDismissed happens to do; today that reread also unmounts this.
			setOpen(false)
			onDismissed()
		} catch (error: any) {
			notification.error(error.Message || "Failed to dismiss the message")
		} finally {
			setDismissing(false)
		}
	}

	return (
		<>
			<Divider />
			<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
				<Typography variant="body2" color="textSecondary">
					No customer to serve?
				</Typography>
				<Button size="small" color="error" startIcon={<BlockIcon />} onClick={openDialog}>
					Dismiss message
				</Button>
			</Box>

			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{handingOff ? "Hand this message off" : "Dismiss this message"}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" mb={2}>
						{handingOff
							? "It leaves the queue and the thread carries on."
							: "It leaves the queue. The thread is untouched and it can still be assigned later."}
					</Typography>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<FormControl fullWidth size="small">
							<InputLabel id="dismiss-reason-label">Reason</InputLabel>
							<Select
								labelId="dismiss-reason-label"
								label="Reason"
								value={reason}
								onChange={(event) => chooseReason(event.target.value)}
							>
								{dismissalReasonIDs.map((id) => (
									<MenuItem key={id} value={id}>
										{phraseDismissalReason(id)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Note (optional)"
							size="small"
							fullWidth
							multiline
							minRows={2}
							value={note}
							onChange={(event) => setNote(event.target.value)}
						/>
						{handOffOffered && (
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={handOffTicked}
										onChange={(event) => setHandOffTicked(event.target.checked)}
									/>
								}
								label="Keep it open outside Collect to action"
							/>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button variant="text" onClick={() => setOpen(false)} disabled={dismissing}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color={handingOff ? "primary" : "error"}
						disableElevation
						disabled={!reason || dismissing}
						onClick={handleDismiss}
					>
						{handingOff ? "Hand off" : "Dismiss"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export {DismissPanel}
